import { useState, useCallback } from 'react';

export const useSelection = (seats, getSeatAtPosition, GRID_ROWS, GRID_COLS) => {
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const [emptyAreaSelection, setEmptyAreaSelection] = useState(null);
  const [isSelectingEmptyArea, setIsSelectingEmptyArea] = useState(false);

  const getSelectionBox = useCallback((start, end) => {
    if (!start || !end) return null;

    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);

    return { minRow, maxRow, minCol, maxCol };
  }, []);

  const getSeatsInBox = useCallback(
    (box) => {
      if (!box) return [];

      const seatsInBox = [];
      for (let row = box.minRow; row <= box.maxRow; row++) {
        for (let col = box.minCol; col <= box.maxCol; col++) {
          const seat = getSeatAtPosition(row, col);
          if (seat) {
            seatsInBox.push(`${row}-${col}`);
          }
        }
      }
      return seatsInBox;
    },
    [getSeatAtPosition],
  );

  const getEmptyPositionsInBox = useCallback(
    (box) => {
      if (!box) return [];

      const emptyPositions = [];
      for (let row = box.minRow; row <= box.maxRow; row++) {
        for (let col = box.minCol; col <= box.maxCol; col++) {
          const seat = getSeatAtPosition(row, col);
          if (!seat && row >= 0 && row < GRID_ROWS && col >= 0 && col < GRID_COLS) {
            emptyPositions.push({ row, col });
          }
        }
      }
      return emptyPositions;
    },
    [getSeatAtPosition, GRID_ROWS, GRID_COLS],
  );

  const toggleSeatSelection = useCallback((row, col) => {
    const seatKey = `${row}-${col}`;
    setSelectedSeats((prev) =>
      prev.includes(seatKey) ? prev.filter((key) => key !== seatKey) : [...prev, seatKey],
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSeats([]);
    setEmptyAreaSelection(null);
    setSelectionBox(null);
    setIsSelecting(false);
    setIsSelectingEmptyArea(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, []);

  const startSelection = useCallback((pos) => {
    setIsSelecting(true);
    setIsSelectingEmptyArea(false);
    setSelectionStart(pos);
    setSelectionEnd(pos);
    setSelectionBox(null);
    setEmptyAreaSelection(null);
  }, []);

  const updateSelection = useCallback(
    (pos) => {
      setSelectionEnd(pos);
      const box = getSelectionBox(selectionStart, pos);
      setSelectionBox(box);
    },
    [selectionStart, getSelectionBox],
  );

  const finishSelection = useCallback(
    (e) => {
      if (isSelecting && selectionBox) {
        const seatsInBox = getSeatsInBox(selectionBox);
        const emptyPositions = getEmptyPositionsInBox(selectionBox);

        if (seatsInBox.length === 0 && emptyPositions.length > 0) {
          setEmptyAreaSelection(selectionBox);
          setIsSelectingEmptyArea(true);
        } else if (seatsInBox.length > 0) {
          if (e.ctrlKey || e.metaKey || e.shiftKey) {
            setSelectedSeats((prev) => [...new Set([...prev, ...seatsInBox])]);
          } else {
            setSelectedSeats(seatsInBox);
          }
          setIsSelectingEmptyArea(false);
        }
      }

      setIsSelecting(false);
      setIsSelectingEmptyArea(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      setSelectionBox(null);
    },
    [isSelecting, selectionBox, getSeatsInBox, getEmptyPositionsInBox],
  );

  return {
    selectedSeats,
    setSelectedSeats,
    isSelecting,
    setIsSelecting,
    selectionStart,
    selectionEnd,
    selectionBox,
    emptyAreaSelection,
    setEmptyAreaSelection,
    isSelectingEmptyArea,
    setIsSelectingEmptyArea,
    getSelectionBox,
    getSeatsInBox,
    getEmptyPositionsInBox,
    toggleSeatSelection,
    clearSelection,
    startSelection,
    updateSelection,
    finishSelection,
  };
};
